import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Download, Trash2, Mail, Phone, Loader2, Inbox } from "lucide-react";
import api, { API, formatApiError } from "../../lib/api";
import { ENQUIRY_STATUSES } from "../../constants/content";

const STATUS_STYLES = {
  new: "bg-gold/15 text-golddark",
  contacted: "bg-blue-50 text-blue-700",
  quoting: "bg-orange-50 text-orange-700",
  won: "bg-green-50 text-green-700",
  lost: "bg-slate-100 text-slate-500",
  archived: "bg-slate-100 text-slate-400",
};

export default function EnquiriesTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/enquiries").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setItems(items.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry and its uploaded files? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setItems(items.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (items === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  if (items.length === 0) {
    return (
      <div data-testid="enquiries-empty" className="bg-white border border-line px-8 py-16 text-center">
        <Inbox className="w-10 h-10 text-line mx-auto mb-4" />
        <p className="font-display font-semibold text-lg text-ink">No enquiries yet</p>
        <p className="text-sm text-slate2 mt-2">New website enquiries and uploaded plans will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="enquiries-list">
      {items.map((e) => (
        <div key={e.id} className={`bg-white border ${e.urgent ? "border-[#B3261E]/50 border-l-4 border-l-[#B3261E]" : "border-line"}`}>
          <button onClick={() => setOpen(open === e.id ? null : e.id)} data-testid={`enquiry-row-${e.id}`} className="w-full flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-4 text-left">
            {e.urgent && <span className="font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 bg-[#B3261E] text-white" data-testid={`enquiry-urgent-badge-${e.id}`}>Urgent{e.urgency ? ` — ${e.urgency}` : ""}</span>}
            <span className={`font-mono text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`} data-testid={`enquiry-status-${e.id}`}>{e.status}</span>
            <span className="font-display font-semibold text-ink">{e.name}</span>
            <span className="text-xs text-slate2">{e.project_type || "General"}{e.address ? ` — ${e.address}` : ""}</span>
            {e.files?.length > 0 && <span className="font-mono text-[10px] text-golddark uppercase tracking-wider">{e.files.length} file{e.files.length > 1 ? "s" : ""}</span>}
            <span className="ml-auto text-xs text-slate2/70">{new Date(e.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short" })}</span>
            {open === e.id ? <ChevronUp className="w-4 h-4 text-slate2" /> : <ChevronDown className="w-4 h-4 text-slate2" />}
          </button>

          {open === e.id && (
            <div className="border-t border-line px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid={`enquiry-detail-${e.id}`}>
              <div className="space-y-2.5 text-sm">
                {e.urgent && (
                  <p className="bg-[#B3261E]/10 border border-[#B3261E]/30 text-[#B3261E] font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-2 inline-block" data-testid={`enquiry-urgent-detail-${e.id}`}>
                    Urgent building enquiry{e.urgency ? ` — ${e.urgency}` : ""}
                  </p>
                )}
                <p><span className="text-slate2">Email: </span>{e.email ? <a href={`mailto:${e.email}`} className="text-golddark hover:underline inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{e.email}</a> : <span className="text-slate2/70">Not provided — phone contact</span>}</p>
                <p><span className="text-slate2">Phone: </span><a href={`tel:${e.phone}`} className="text-golddark hover:underline inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{e.phone}</a></p>
                {e.address && <p><span className="text-slate2">Address / Suburb: </span>{e.address}</p>}
                {e.budget && <p><span className="text-slate2">Budget: </span>{e.budget}</p>}
                {e.timeframe && <p><span className="text-slate2">Start: </span>{e.timeframe}</p>}
                <p className="text-xs text-slate2/70">Received {new Date(e.created_at).toLocaleString("en-NZ")}{e.email_sent ? " · email notification sent" : " · email notification failed"}</p>
                {e.message && <p className="mt-3 bg-paper border border-line px-4 py-3 whitespace-pre-wrap text-ink/85"><span className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate2 block mb-1.5">{e.urgent ? "What happened" : "Project details"}</span>{e.message}</p>}
              </div>
              <div>
                {e.files?.length > 0 && (
                  <div className="mb-5">
                    <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate2 mb-2">Uploaded plans & files</p>
                    <ul className="space-y-2">
                      {e.files.map((f) => (
                        <li key={f.id}>
                          <a href={`${API}/files/${e.id}/${f.id}?auth=${localStorage.getItem("rb_token")}`} data-testid={`enquiry-file-${f.id}`} className="inline-flex items-center gap-2 text-sm text-golddark hover:underline">
                            <Download className="w-3.5 h-3.5" /> {f.filename}
                            <span className="text-xs text-slate2/70">({f.size >= 1048576 ? `${(f.size / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(f.size / 1024))} KB`})</span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  <label className="font-mono text-[10px] tracking-[0.18em] uppercase text-slate2">Status</label>
                  <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} data-testid={`enquiry-status-select-${e.id}`} className="border border-line px-3 py-2 text-sm bg-white focus:outline-none focus:border-gold">
                    {ENQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                  <button onClick={() => remove(e.id)} data-testid={`enquiry-delete-${e.id}`} className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-800 ml-auto">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
